import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { Chip } from '@/components/forge/Chip'
import { ForgeButton } from '@/components/forge/ForgeButton'
import { Icon } from '@/components/forge/Icon'
import { Panel } from '@/components/forge/Panel'
import { EmptyState, ErrorState, SkeletonRows } from '@/components/forge/States'
import { PAGE_SIZES, TablePagination } from '@/components/forge/TablePagination'
import { Mono, Num } from '@/components/forge/primitives'
import { DOC_TYPES, DOC_VIEWS } from '@/features/documents/api'
import {
  useCreateDocument,
  useDocumentAccess,
  useDocuments,
  useSaveDocument,
} from '@/features/documents/queries'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { useToast } from '@/providers/ToastProvider'
import { formatDateTime, formatInteger } from '@/lib/format'
import { ON_TINT_CLASS, TINT_CLASS } from '@/lib/tone'
import { cn } from '@/lib/cn'
import { DocumentDrawer } from './DocumentDrawer'
import { DocumentFormDialog } from './DocumentFormDialog'
import { REVISION_STATUS_TONE, lookOf } from './documentLook'

/**
 * Documents & SOPs — the knowledge register. LIVE.
 *
 * `/v1/documents`, the whole resource. The screen stays under Data Fusion; the
 * collection is a top-level one and no longer a sub-path of this module.
 *
 * ── The scope column is gone, and attachments took its place ─────────────
 * The demo's organising idea was SCOPE — a document was enterprise-wide, or a
 * site's, or a line's, or one machine's, derived from the shallowest thing it
 * linked to. The API has no such field, and two of the five values it used
 * cannot exist server-side at all: `at_least_one_attachment` refuses a document
 * with no attachments, which is what both "enterprise-wide" and "unplaced"
 * were.
 *
 * What the register shows instead is what a document is ATTACHED TO, which is
 * the fact the API holds — and it is a better column anyway, because a document
 * may be attached to a mix of sites, lines, machines, asset classes and sensors
 * at once. The demo could not express that. See `features/documents/api.js`.
 *
 * ── The filter is one attachment at a time, because the wire is ──────────
 * `list_documents` takes `plant_id`, `line_id`, `machine_id`, `asset_class_id`
 * and `sensor_id` as separate params and ANDs whichever are set. Narrowing to
 * "this machine" is what somebody standing at a machine wants; narrowing to
 * "this machine or its line" is not a query the endpoint has.
 *
 * ── The search matches the NAME alone ────────────────────────────────────
 * `apply_search(query, search, Document.name)`. The demo's box also matched the
 * id, the owner and the tags; two of those are not columns. Said beside the box
 * rather than left for somebody to type an owner and read the empty result as
 * "no such document".
 */

const ALL = 'all'

export function DocumentsPage() {
  const { t } = useTranslation(['app', 'common'])
  const { toast } = useToast()
  useDocumentTitle(`${t('dataFusion.sections.documents')} · ${t('common:brand.name')}`)

  const access = useDocumentAccess()

  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  const [documentType, setDocumentType] = useState(ALL)
  const [view, setView] = useState(DOC_VIEWS[0])
  const [page, setPage] = useState(1)
  /* `PAGE_SIZES[0]` and not the API's own default: the pager offers a fixed
     set of sizes and starting on one outside it would show a control that
     cannot return to where it began. The server's own default when the key is
     absent is `DEFAULT_PAGE_SIZE` in features/documents/api.js, which is what
     `fetchDocuments` falls back to and is never what this screen sends. */
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0])

  /**
   * The open document lives in the URL, as `?doc=<id>`.
   *
   * ── It was local state, and that made the register unlinkable ──────────
   * Anything wanting to send somebody to one document could only send them to
   * the list — which is what the Anomaly Action tab's Referenced Documents rows
   * did: they cited a specific revision of a specific file and then dropped the
   * reader on an unfiltered register to find it themselves. Any other module
   * citing a document had the same problem, and so did sharing a link to one.
   *
   * ── Why a param and not a route segment ───────────────────────────────
   * `DocumentDrawer` is a drawer over this list, not a page: the register stays
   * mounted and visible behind it, the search and filters survive, and Back
   * closes it rather than leaving the module. `?doc=` matches the shape
   * `WorkOrdersPage` already uses for `?wo=`, so the two deep links behave the
   * same way.
   *
   * ── No existence check here, unlike the work-order board ──────────────
   * That one drops `?wo=` when the id is not in the loaded page, because its
   * drawer renders from the row. This drawer takes an id and fetches it
   * (`useDocument`), so a document hidden by the current filter, or on page
   * four, still opens — and a 404 is the drawer's own error state rather than a
   * param that silently clears itself.
   */
  const [searchParams, setSearchParams] = useSearchParams()
  const openId = searchParams.get('doc')
  const setOpenId = useCallback(
    (id) =>
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous)
          if (id) next.set('doc', id)
          else next.delete('doc')
          return next
        },
        /* Replace, not push. Opening and closing a drawer should not fill the
           back stack with the same list five times over. */
        { replace: true },
      ),
    [setSearchParams],
  )
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState(null)

  /* The input is local and the query is the request, debounced. Driving the
     request straight off the field would put one behind every keystroke. */
  useEffect(() => {
    if (search === query) return
    const timer = setTimeout(() => {
      setQuery(search)
      setPage(1)
    }, 300)
    return () => clearTimeout(timer)
  }, [search, query])

  const docs = useDocuments(
    { documentType, search: query || undefined, page, pageSize },
    { enabled: access.canRead },
  )

  const create = useCreateDocument()
  const save = useSaveDocument()

  const rows = docs.data?.items ?? []
  const counts = docs.data?.counts ?? {}
  const total = docs.data?.total ?? 0

  /**
   * The number the header and the "of" in the match line both read.
   *
   * Summed from `counts.document_type` rather than taken from `total`, because
   * the two are computed at different points: `counts` is over the set BEFORE
   * the type filter applies, `total` after it. So this does not move when a
   * type chip is pressed, which is what makes "3 of 12" mean anything.
   *
   * It DOES move with the search, because the server searches before it counts.
   * That is correct and it is why the header says so — during a search this is
   * "12 documents match", not "12 documents in the register", and wording it
   * the second way would be a claim about the library taken from a filter.
   */
  const scopedTotal = Object.values(counts).reduce((sum, count) => sum + count, 0)
  const searching = query.trim().length > 0

  const isNarrowed = query.trim().length > 0 || documentType !== ALL

  function clearFilters() {
    setSearch('')
    setQuery('')
    setDocumentType(ALL)
    setPage(1)
  }

  const narrowType = (next) => {
    setDocumentType(next)
    setPage(1)
  }

  const closeForm = () => {
    setCreating(false)
    setEditing(null)
    create.reset()
    save.reset()
  }

  const submit = (payload) => {
    const mutation = payload.id ? save : create
    mutation.mutate(payload, {
      onSuccess: (saved) => {
        closeForm()
        toast(
          t(payload.id ? 'dataFusion.docs.toast.saved' : 'dataFusion.docs.toast.filed', {
            name: saved.name,
          }),
        )
      },
      /* No toast on failure. Every refusal this endpoint gives is specific and
         belongs beside the control that caused it — the duplicate-content one
         in particular needs the person back on step 1, which a toast cannot
         do. The dialog renders `error` itself. */
    })
  }

  const formError = create.error ?? save.error

  return (
    <div className="px-8 py-6 max-xl:px-5.5">
      <header className="mb-4 flex flex-wrap items-start justify-between gap-x-5 gap-y-3">
        <div className="min-w-0">
          <h1 className="m-0 text-headline">{t('dataFusion.sections.documents')}</h1>
          <p className="mt-2 mb-0 max-w-[72ch] text-body-sm text-ink-dim">
            <Num>
              {t(searching ? 'dataFusion.docs.summaryMatching' : 'dataFusion.docs.summary', {
                total: formatInteger(scopedTotal),
              })}
            </Num>
          </p>
        </div>

        <div className="flex items-center gap-2.5">
          {/* Two densities of the same list. The list is the default because a
              register is read by column — which revision, when does it expire,
              what is it attached to — and cards make every one a saccade. */}
          <div role="group" aria-label={t('dataFusion.docs.viewLabel')} className="flex gap-1.5">
            {DOC_VIEWS.map((option) => (
              <Chip key={option} selected={view === option} onClick={() => setView(option)}>
                {t(`dataFusion.docs.view.${option}`)}
              </Chip>
            ))}
          </div>
          <ForgeButton
            variant="primary"
            disabled={!access.canCreate}
            onClick={() => setCreating(true)}
          >
            <Icon name="up" size="xs" />
            {t('dataFusion.docs.form.action')}
          </ForgeButton>
        </div>
      </header>

      {access.isPending ? (
        <SkeletonRows count={6} />
      ) : !access.canRead ? (
        <EmptyState
          icon="lock"
          title={t('dataFusion.docs.forbidden')}
          body={t('dataFusion.docs.forbiddenBody')}
        />
      ) : docs.isError ? (
        <ErrorState
          error={docs.error}
          title={t('dataFusion.docs.loadFailed')}
          onRetry={docs.refetch}
        />
      ) : (
        <>
          {/* ── The type strip ──────────────────────────────────────────
              All eight, always, each with its count. The demo showed only the
              types that had documents, on the reasoning that a chip filtering
              to nothing wastes a click — but a register whose filter row
              changes shape as it fills is one nobody can learn, and "Policy 0"
              is a useful thing to be able to read.

              The counts come from `counts.document_type`, computed server-side
              over the searched set BEFORE the type filter applies. So a chip
              always says how many it would show, and pressing one moves the
              rows without rewriting the strip. */}
          <div
            role="group"
            aria-label={t('dataFusion.docs.filterByType')}
            className="mb-3 flex flex-wrap gap-2"
          >
            <Chip selected={documentType === ALL} onClick={() => narrowType(ALL)}>
              <Num>{t('dataFusion.docs.allTypes', { count: scopedTotal })}</Num>
            </Chip>
            {DOC_TYPES.map((entry) => {
              const look = lookOf(entry)
              const selected = documentType === entry
              return (
                <Chip
                  key={entry}
                  selected={selected}
                  onClick={() => narrowType(selected ? ALL : entry)}
                >
                  <Icon
                    name={look.icon}
                    size="xs"
                    className={cn(
                      'me-1.5',
                      selected ? undefined : (ON_TINT_CLASS[look.tone] ?? 'text-ink-mute'),
                    )}
                  />
                  <Num>
                    {t('dataFusion.docs.typeChip', {
                      label: t(`dataFusion.docs.type.${entry}`),
                      count: counts[entry] ?? 0,
                    })}
                  </Num>
                </Chip>
              )
            })}
          </div>

          <Panel flush className="mb-3.5 px-3.5 py-2.5">
            <div className="flex flex-wrap items-center gap-x-2.5 gap-y-2">
              <div className="flex min-w-[14rem] flex-1 items-center gap-2 rounded-sm border border-hairline bg-card px-3 py-1.5 focus-within:border-forge max-sm:w-full max-sm:flex-none">
                <Icon name="search" size="sm" className="text-ink-mute" />
                <label className="sr-only" htmlFor="doc-search">
                  {t('dataFusion.docs.searchLabel')}
                </label>
                <input
                  id="doc-search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder={t('dataFusion.docs.searchPlaceholder')}
                  className="min-w-0 flex-1 border-0 bg-transparent text-body-sm text-ink outline-none placeholder:text-ink-dim"
                />
              </div>

              {/* What the box actually matches, said rather than discovered.
                  The demo searched the id, the owner and the tags too; the
                  server matches `Document.name` and nothing else. */}
              <span className="text-micro font-normal text-ink-dim">
                {t('dataFusion.docs.searchScope')}
              </span>

              {isNarrowed ? (
                <ForgeButton variant="ghost" size="sm" onClick={clearFilters}>
                  {t('common:actions.clearFilters')}
                </ForgeButton>
              ) : null}

              <p className="m-0 ms-auto text-body-sm text-ink-dim">
                <Num>
                  {t('dataFusion.docs.matchCount', {
                    shown: formatInteger(total),
                    total: formatInteger(scopedTotal),
                  })}
                </Num>
              </p>
            </div>
          </Panel>

          {docs.isPending ? (
            <SkeletonRows count={6} />
          ) : rows.length === 0 ? (
            <EmptyState
              icon="doc"
              title={isNarrowed ? t('dataFusion.docs.noMatch') : t('dataFusion.docs.emptyTitle')}
              body={isNarrowed ? t('dataFusion.docs.noMatchBody') : t('dataFusion.docs.emptyBody')}
              action={
                isNarrowed ? (
                  <ForgeButton onClick={clearFilters}>
                    {t('common:actions.clearFilters')}
                  </ForgeButton>
                ) : access.canCreate ? (
                  <ForgeButton variant="primary" onClick={() => setCreating(true)}>
                    {t('dataFusion.docs.form.action')}
                  </ForgeButton>
                ) : null
              }
            />
          ) : (
            <>
              {view === 'list' ? (
                <Panel flush className="overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <caption className="sr-only">{t('dataFusion.docs.tableCaption')}</caption>
                      <thead>
                        <tr>
                          {['document', 'type', 'attached', 'revision', 'effective', 'expiry', 'status'].map(
                            (column) => (
                              <th
                                key={column}
                                scope="col"
                                className="eyebrow bg-sunken px-3.5 py-2.5 text-start whitespace-nowrap"
                              >
                                {t(`dataFusion.docs.columns.${column}`)}
                              </th>
                            ),
                          )}
                          <th scope="col" className="bg-sunken px-3.5 py-2.5">
                            <span className="sr-only">{t('dataFusion.docs.columns.actions')}</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((doc) => (
                          <tr key={doc.id} className="border-t border-subtle hover:bg-sunken">
                            <td className="px-3.5 py-2.5">
                              <span className="flex items-center gap-2.5">
                                <DocThumb doc={doc} />
                                <span className="min-w-0 text-body-sm font-semibold text-ink">
                                  {doc.name}
                                </span>
                              </span>
                            </td>
                            <td className="px-3.5 py-2.5 text-body-sm text-ink-dim">
                              {t(`dataFusion.docs.type.${doc.documentType}`)}
                            </td>
                            <td className="px-3.5 py-2.5 text-body-sm text-ink-dim">
                              {/* A COUNT, because that is what the list item
                                  carries. `attachments` with their names and
                                  kinds is on the detail read alone, and a
                                  request per row to draw chips here would be
                                  fifty requests for one page. */}
                              <Num>
                                {t('dataFusion.docs.attachedCount', {
                                  count: doc.attachmentCount,
                                })}
                              </Num>
                            </td>
                            <td className="px-3.5 py-2.5 text-body-sm text-ink-dim">
                              <Mono>
                                <Num>{doc.currentRevision}</Num>
                              </Mono>
                            </td>
                            <td className="px-3.5 py-2.5 text-body-sm text-ink-dim">
                              <Num>
                                {formatDateTime(doc.effectiveDate, { dateStyle: 'medium' })}
                              </Num>
                            </td>
                            <td className="px-3.5 py-2.5 text-body-sm">
                              <ExpiryCell expiryDate={doc.expiryDate} />
                            </td>
                            <td className="px-3.5 py-2.5">
                              <RevisionStatusPill revision={doc.latestRevision} />
                            </td>
                            <td className="px-3.5 py-2.5 text-end">
                              <ForgeButton size="sm" onClick={() => setOpenId(doc.id)}>
                                {t('dataFusion.docs.open')}
                              </ForgeButton>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <TablePagination
                    total={total}
                    page={docs.data?.page ?? page}
                    pageSize={docs.data?.pageSize ?? pageSize}
                    label={t('dataFusion.sections.documents')}
                    onPageChange={setPage}
                    onPageSizeChange={(next) => {
                      setPageSize(next)
                      setPage(1)
                    }}
                  />
                </Panel>
              ) : (
                <>
                  <ul className="m-0 grid list-none grid-cols-[repeat(auto-fill,minmax(min(100%,17rem),1fr))] gap-3 p-0">
                    {rows.map((doc) => (
                      <li key={doc.id}>
                        <button
                          type="button"
                          onClick={() => setOpenId(doc.id)}
                          className="flex w-full flex-col gap-2.5 rounded-sm border border-hairline bg-card p-3.5 text-start transition-colors hover:border-forge"
                        >
                          <span className="flex items-start gap-2.5">
                            <DocThumb doc={doc} large />
                            <span className="flex min-w-0 flex-1 flex-col gap-1">
                              <span className="text-body-sm font-semibold text-ink">
                                {doc.name}
                              </span>
                              <span className="text-micro font-normal text-ink-dim">
                                {t(`dataFusion.docs.type.${doc.documentType}`)}
                              </span>
                            </span>
                          </span>
                          <span className="flex flex-wrap items-center gap-1.5">
                            <RevisionStatusPill revision={doc.latestRevision} />
                            <span className="text-micro font-normal text-ink-dim">
                              <Num>
                                {t('dataFusion.docs.attachedCount', {
                                  count: doc.attachmentCount,
                                })}
                              </Num>
                            </span>
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>

                  <Panel flush className="mt-3">
                    <TablePagination
                      total={total}
                      page={docs.data?.page ?? page}
                      pageSize={docs.data?.pageSize ?? pageSize}
                      label={t('dataFusion.sections.documents')}
                      onPageChange={setPage}
                      onPageSizeChange={(next) => {
                        setPageSize(next)
                        setPage(1)
                      }}
                    />
                  </Panel>
                </>
              )}
            </>
          )}
        </>
      )}

      <DocumentDrawer
        id={openId}
        access={access}
        onClose={() => setOpenId(null)}
        onEdit={(doc) => {
          setOpenId(null)
          setEditing(doc)
        }}
      />

      {creating || editing ? (
        <DocumentFormDialog
          /* Remounted per opening AND per document, so the steps restart and
             the fields reload without an effect resetting them — the
             arrangement `WorkOrderFormDialog` arrived at after the lint rule
             caught a setState inside an effect. */
          key={editing?.id ?? 'new'}
          open
          document={editing}
          access={access}
          /* Without a manage grant over the owner plant, only the attachments
             can change — the dialog opens on them with the rest locked. */
          attachOnly={Boolean(editing) && !access.canEditDocument(editing)}
          pending={create.isPending || save.isPending}
          error={formError}
          onClose={closeForm}
          onSubmit={submit}
        />
      ) : null}
    </div>
  )
}

/**
 * The file-type thumbnail: the type's glyph over its revision number.
 *
 * The demo put the file EXTENSION under the glyph — DWG, DOC, PDF — on the
 * reasoning that it tells somebody whether they can open it on the machine they
 * are standing at. True, and no longer available: `FilePurpose.document` takes
 * `application/pdf` alone, so every document in this register is a PDF and the
 * label would say the same word eight times. The revision number is the fact
 * worth having in its place, and it is one a register is genuinely read for.
 */
export function DocThumb({ doc, large = false }) {
  const look = lookOf(doc.documentType)
  return (
    <span
      className={cn(
        'flex shrink-0 flex-col items-center justify-center gap-1 rounded-xs border border-hairline',
        look.tone ? TINT_CLASS[look.tone] : 'bg-sunken',
        large ? 'h-16 w-[3.4rem]' : 'h-9 w-[1.9rem]',
      )}
    >
      <Icon
        name={look.icon}
        size={large ? 'md' : 'xs'}
        className={look.tone ? ON_TINT_CLASS[look.tone] : 'text-ink-mute'}
      />
      <span
        className={cn(
          'font-semibold tracking-wide',
          large ? 'text-micro' : 'text-[0.5rem]',
          look.tone ? ON_TINT_CLASS[look.tone] : 'text-ink-mute',
        )}
      >
        <Num>r{doc.currentRevision}</Num>
      </span>
    </span>
  )
}

/**
 * The latest revision's ingestion status.
 *
 * ── This is NOT the demo's Verified pill ─────────────────────────────────
 * That one meant "a person has checked this revision", and it had no column —
 * no field, no route, nothing behind the PATCH the drawer called its only
 * write. This means "the AI engine has read the file", which is a real
 * `DocumentRevisionStatus` the API serves and which nothing displayed before.
 *
 * They are different claims and this one is not dressed as the other. The human
 * sign-off is in BACKEND-GAPS.md.
 */
export function RevisionStatusPill({ revision }) {
  const { t } = useTranslation('app')
  if (!revision) return null

  const tone = REVISION_STATUS_TONE[revision.status]
  return (
    <span
      className={cn(
        'inline-flex w-fit items-center rounded-pill px-2.5 py-[3px] text-micro font-semibold whitespace-nowrap',
        tone ? cn(TINT_CLASS[tone], ON_TINT_CLASS[tone]) : 'bg-sunken text-ink-dim',
      )}
      title={revision.status === 'failed' ? (revision.error ?? undefined) : undefined}
    >
      {t(`dataFusion.docs.revisionStatus.${revision.status}`)}
    </span>
  )
}

/**
 * When a document stops applying.
 *
 * Coloured only once it has actually expired, and not before. `expiry_date` is
 * nullable and most documents have none, so an amber "expiring soon" band would
 * paint most of the register a warning colour for a state nobody has to act on
 * — and the API gives no threshold for what "soon" means.
 */
export function ExpiryCell({ expiryDate }) {
  const { t } = useTranslation('app')
  if (!expiryDate) {
    return <span className="text-micro font-normal text-ink-mute italic">{t('dataFusion.docs.noExpiry')}</span>
  }

  const expired = expiryDate < todayISO()
  return (
    <Num className={expired ? 'font-semibold text-on-red' : 'text-ink-dim'}>
      {formatDateTime(expiryDate, { dateStyle: 'medium' })}
    </Num>
  )
}

/** Today as `YYYY-MM-DD`, in the reader's own zone — the day an expiry means. */
function todayISO() {
  const now = new Date()
  return new Date(now.getTime() - now.getTimezoneOffset() * 60_000).toISOString().slice(0, 10)
}
